/**
 * 🎓 DIAGNOSTIC D'ENTRÉE - COMPOSANT UTILISATEUR
 * ===============================================
 * 
 * Questionnaire de 5 questions pour évaluer le niveau initial.
 * Affiché automatiquement avant le premier module.
 * 
 * Qualiopi Indicateur 11 - Adaptation aux publics bénéficiaires
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Brain, CheckCircle2, ChevronRight, ChevronLeft, 
  Target, Award, Sparkles, Lock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { getBackendUrl } from '../config/environment';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

/**
 * Composant Diagnostic d'Entrée
 */
const EntryDiagnostic = ({ onComplete, onSkip }) => {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [existingScore, setExistingScore] = useState(null);

  useEffect(() => {
    checkStatusAndLoadQuestions();
  }, []);

  const checkStatusAndLoadQuestions = async () => {
    try {
      setLoading(true);
      
      // Vérifier si déjà complété
      const statusRes = await axios.get(`${API}/diagnostic/entry/status`);
      if (statusRes.data.has_entry_diagnostic) {
        setAlreadyCompleted(true);
        setExistingScore(statusRes.data.entry_score);
        setLoading(false);
        return;
      }
      
      // Charger les questions (diagnostic d'entrée = GRATUIT, pas de vérification sessions)
      const res = await axios.get(`${API}/diagnostic/entry/questions`);
      setQuestions(res.data.questions || []);
    } catch (err) {
      console.error('Erreur chargement diagnostic:', err);
      toast.error('Erreur lors du chargement du diagnostic');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const answersArray = Object.entries(answers).map(([question_id, value]) => ({
        question_id,
        value
      }));
      
      const res = await axios.post(`${API}/diagnostic/entry/submit`, {
        answers: answersArray
      });
      
      toast.success(`Diagnostic complété ! Votre score: ${res.data.percentage}%`);
      
      if (onComplete) {
        onComplete(res.data);
      }
    } catch (err) {
      console.error('Erreur soumission diagnostic:', err);
      toast.error('Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
  const isLastQuestion = currentIndex === questions.length - 1;
  const canSubmit = Object.keys(answers).length === questions.length;

  if (loading) {
    return (
      <Card className="max-w-2xl mx-auto border border-[#FFD86F]/20 bg-white/80 backdrop-blur-xl">
        <CardContent className="p-8">
          <div className="flex items-center justify-center space-x-3">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-8 h-8 border-3 border-[#FFD86F] border-t-transparent rounded-full"
            />
            <span className="text-gray-600">Chargement du diagnostic...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (alreadyCompleted) {
    return (
      <Card className="max-w-2xl mx-auto border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
        <CardContent className="p-8 text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-green-700 mb-2">Diagnostic déjà complété</h3>
          <p className="text-green-600 mb-4">
            Votre score initial : <span className="font-bold text-2xl">{existingScore}%</span>
          </p>
          <Button onClick={onSkip} className="bg-green-600 hover:bg-green-700">
            Continuer vers la formation
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      <Card className="border border-[#FFD86F]/20 bg-white/90 backdrop-blur-xl shadow-xl overflow-hidden">
        {/* Header */}
        <CardHeader className="bg-gradient-to-r from-[#3B1E73] to-[#8B5CF6] text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Brain className="w-6 h-6" />
            </div>
            <div>
              <CardTitle className="text-xl">Diagnostic d&apos;entrée</CardTitle>
              <p className="text-white/80 text-sm mt-1">
                Évaluez votre niveau actuel en 5 questions
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-500 mb-2">
              <span>Question {currentIndex + 1} sur {questions.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Question */}
          <AnimatePresence mode="wait">
            {currentQuestion && (
              <motion.div
                key={currentQuestion.question_id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-lg font-semibold text-[#2B1854] mb-6">
                  {currentQuestion.question}
                </h3>

                <div className="space-y-3">
                  {currentQuestion.options.map((option) => (
                    <motion.button
                      key={option.value}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={() => handleAnswer(currentQuestion.question_id, option.value)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                        answers[currentQuestion.question_id] === option.value
                          ? 'border-[#8B5CF6] bg-[#8B5CF6]/10 text-[#3B1E73]'
                          : 'border-gray-200 hover:border-[#FFD86F]/50 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          answers[currentQuestion.question_id] === option.value
                            ? 'border-[#8B5CF6] bg-[#8B5CF6]'
                            : 'border-gray-300'
                        }`}>
                          {answers[currentQuestion.question_id] === option.value && (
                            <CheckCircle2 className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <span className="text-sm">{option.label}</span>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Précédent
            </Button>

            {isLastQuestion ? (
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className="gap-2 bg-gradient-to-r from-[#FFD86F] to-[#FFB347] text-[#2B1854] hover:shadow-lg"
              >
                {submitting ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-4 h-4 border-2 border-current border-t-transparent rounded-full"
                  />
                ) : (
                  <Award className="w-4 h-4" />
                )}
                Terminer le diagnostic
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={!answers[currentQuestion?.question_id]}
                className="gap-2 bg-[#3B1E73] hover:bg-[#2B1854]"
              >
                Suivant
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Skip option */}
          {onSkip && (
            <div className="text-center mt-6">
              <button
                onClick={onSkip}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Passer pour l&apos;instant
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Qualiopi */}
      <p className="text-center text-xs text-gray-400 mt-4">
        <Lock className="w-3 h-3 inline mr-1" />
        Ce diagnostic est conforme aux exigences Qualiopi (Indicateur 11)
      </p>
    </motion.div>
  );
};

export default EntryDiagnostic;
